import { useEffect } from 'react'

import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { F0Box, F0Button, F0Dialog, F0Heading, F0Text } from '@factorialco/f0-react'
import { ChevronDown, ChevronUp } from '@factorialco/f0-react/icons/app'
import { EventSidepanelNavigationDocument, SessionFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

type Sidepanels = 'attendance'

const EventSidepanelNavigation = ({ sidepanel }: { sidepanel: Sidepanels }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id, classId, eventId } = useParams()
  const { data, loading, error } = useQuery(EventSidepanelNavigationDocument, {
    variables: { trainingClassIds: [String(classId)] },
  })

  const currentEvent = data?.trainings.sessionsConnection.nodes.find(
    (session) => session.id === String(eventId)
  )

  const redirectPath =
    !loading && (error || !currentEvent || !data)
      ? navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
          id: String(id),
          classId: String(classId),
        })
      : null

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath)
    }
  }, [navigate, redirectPath])

  if (loading) return null
  if (redirectPath) return null
  if (!currentEvent || !data) return null

  return (
    <F0Dialog
      isOpen
      position="right"
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
            id: String(id),
            classId: String(classId),
          })
        )
      }
      title={
        <Header
          currentEvent={currentEvent}
          events={data.trainings.sessionsConnection.nodes}
          sidepanel={sidepanel}
        />
      }
    >
      <Outlet />
    </F0Dialog>
  )
}

const Header = ({
  currentEvent,
  events,
}: {
  currentEvent: SessionFragment
  events: SessionFragment[]
  sidepanel: Sidepanels
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id, classId } = useParams()
  const currentIndex = events.indexOf(currentEvent)

  const clampIndex = (index: number) => {
    if (index < 0) return events[events.length - 1]?.id
    if (index >= events.length) return events[0]?.id

    return events[index]?.id
  }
  const prevEvent = clampIndex(currentIndex - 1)
  const nextEvent = clampIndex(currentIndex + 1)

  const getNavigation = (eventId: string) => {
    return navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.attendance.event.fullPath(
      { id: String(id), classId: String(classId), eventId: String(eventId) }
    )
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="2xl">
      {events.length > 1 && (
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
          <F0Button
            variant="outline"
            size="sm"
            icon={ChevronUp}
            hideLabel
            label={i18n.t('common.pagination.previous')}
            onClick={() => navigate(getNavigation(String(prevEvent)))}
          />
          <F0Button
            variant="outline"
            size="sm"
            icon={ChevronDown}
            hideLabel
            label={i18n.t('common.pagination.next')}
            onClick={() => navigate(getNavigation(String(nextEvent)))}
          />
          <F0Text variant="small" content={`${currentIndex + 1}/${events.length}`} />
        </F0Box>
      )}
      <F0Heading as="h2" variant="heading" content={currentEvent.name} />
    </F0Box>
  )
}

export default EventSidepanelNavigation
